import { type FC, type SelectHTMLAttributes, useId } from "react";

type Props = Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> & {
  label: string;
  hint?: string;
  error?: string;
  /** select の選択肢。ラベル = value のシンプルなパターンに揃える (= 大半のユースケース)。 */
  options: readonly string[] | readonly { value: string; label: string }[];
};

const isObjectOption = (
  options: Props["options"],
): options is readonly { value: string; label: string }[] =>
  options.length > 0 && typeof options[0] !== "string";

/**
 * `Field` の select 版。string 配列 (= label = value) と { value, label } 配列の両対応。
 */
export const FieldSelect: FC<Props> = ({ label, hint, error, options, id, className, ...rest }) => {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  return (
    <div className="space-y-1.5">
      <label htmlFor={fieldId} className="block text-neutral-700 text-sm">
        {label}
      </label>
      <select
        id={fieldId}
        // mobile は text-base (16px)、desktop (sm:) で text-sm に戻す。
        // iOS Safari の select も font-size < 16px で focus 時に自動ズームするため。
        className={`block w-full rounded border bg-white px-3 py-2 text-base text-neutral-900 focus:outline-none focus:ring-1 sm:text-sm ${error ? "border-red-500 focus:border-red-500 focus:ring-red-500" : "border-neutral-300 focus:border-neutral-900 focus:ring-neutral-900"} ${className ?? ""}`}
        {...rest}
      >
        {isObjectOption(options)
          ? options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))
          : (options as readonly string[]).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
      </select>
      {hint && !error && <p className="text-neutral-500 text-xs">{hint}</p>}
      {error && <p className="text-red-600 text-xs">{error}</p>}
    </div>
  );
};
